import { useCallback, useLayoutEffect, useState } from 'react'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { Child, getChild } from '../common/child'
import { getCategories, getHasUsagePermission } from '../common/category'
import { useErrorHandle } from '../common/error/errorHandler'
import { showLoading, notifyMessage } from '../common/store/slices/application'
import { useOperationLog } from '../common/operationLog'
import { OperationId } from '../common/constant/operationLog'
import { yesNo } from '../common/constant/classification'
import { memberUrl } from '../common/constant/appUrl'
import { tabValue } from '../member/memberService'
import { translate } from '../../i18n'
import { GetCategoryMstDto } from '../../dataAccess/webApi/dto/categoryMstDto'
import { GetChildrenHospitalAdmissionDto } from '../../dataAccess/webApi/dto/childrenHospitalAdmissionDto'
import {
  executeGetChildrenHospitalAdmission,
  executePutChildrenHospitalAdmission
} from '../../dataAccess/webApi/dao/childrenHospitalAdmissionDao'
import { undefinedPropsToNull } from '../../utils/objectUtil'

/** カテゴリーキー */
const CATEGORY_KEY = 'hospitalAdmission'
/** サブカテゴリーキー */
const SUB_CATEGORIES = [
  'hospitalAdmission.admissionAge',
  'hospitalAdmission.reason'
]
/** 市民用事業ID（市民用の項目は事業ID'0'） */
const DEFAULT_PROJECT_ID = '0'
/** 市民用施設ID（市民用のデータは施設ID'0'で作成する） */
const DEFAULT_FACILITY_ID = '0'

/** URLパラメータ */
interface UrlParams {
  childId: string
}

/** 入院情報の入力フォームの項目の定義 */
export interface Inputs {
  detailUpdateDatetime: string | null
  hospitalAdmissionFlag: string | null
  hospitalAdmission?: HospitalAdmissionForm[]
}[]

/** 入力フォームの項目の定義 */
export interface HospitalAdmissionForm {
  seq?: number | null
  admissionAge?: string | null
  admissionMonth?: string | null
  reason?: string | null
  deleted?: boolean
}

/** 入力フォームの初期値 */
const FORM_DEFAULT_VALUES: Inputs = {
  detailUpdateDatetime: null,
  hospitalAdmissionFlag: null,
  hospitalAdmission: [],
}

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const history = useHistory()
  const dispatch = useDispatch()

  // URLパラメータ取得
  const { childId } = useParams<UrlParams>()
  // カテゴリーマスタ
  const [categoryMst, setCategoryMst] = useState<GetCategoryMstDto[]>()
  const [hasUsagePermission, setHasUsagePermission] = useState<boolean>(false)
  // リザルトコード
  const [childrenResultCode, setChildrenResultCode] = useState<number>()
  // オペレーションログ
  const { addOperationLog } = useOperationLog()

  // 入力フォーム初期値設定
  const formMethods = useForm<Inputs>({
    defaultValues: FORM_DEFAULT_VALUES,
  })
  const hospitalAdmissionFlag = useWatch({ name: 'hospitalAdmissionFlag', control: formMethods.control })
  const hospitalAdmissions = useWatch({ name: CATEGORY_KEY, control: formMethods.control })
  const { fields, append } = useFieldArray({
    control: formMethods.control,
    name: CATEGORY_KEY
  })

  // 初期処理（ちらつき防止のためにuseLayoutEffect使用）
  useLayoutEffect(() => {
    addOperationLog({
      operationId: OperationId.OP_00000001,
      accessData: [{ userIdRegFlag: yesNo.yes, childId }]
    })
    
    dispatch(
      showLoading(
        errorHandle(async () => {
          const [
            category, 
            child,
            hasUsagePermissionResp,
            hospitalAdmissionResp
          ] = await Promise.all([
            // カテゴリーマスタ取得
            getCategories({projectId: DEFAULT_PROJECT_ID, categories: [CATEGORY_KEY, ...SUB_CATEGORIES]}),
            // お子さま情報
            getChild(childId, DEFAULT_FACILITY_ID),
            getHasUsagePermission(childId),
            // 入院情報情報取得
            executeGetChildrenHospitalAdmission(childId, DEFAULT_FACILITY_ID)
          ])

          // カテゴリーマスタ設定
          setCategoryMst(category)
          setHasUsagePermission(hasUsagePermissionResp)
          // 入力フォームに設定
          formMethods.reset({
            ...formMethods.getValues(),
            ...getInitialInputs(child, hospitalAdmissionResp.result),
          })
        })
      )
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 確定処理
  const onSubmit = useCallback(
    (data: Inputs) => {
      addOperationLog({ operationId: OperationId.OP_00000037 })

      dispatch(
        showLoading({
          process: errorHandle(async () => {
            // 登録情報を設定
            const regData = getRegistHospitalAdmission(data)
            // 登録実行
            const resp = await executePutChildrenHospitalAdmission(childId, regData)
            if (resp.resultCode) {
              // 失敗時
              setChildrenResultCode(resp.resultCode)
            } else {
              // 成功時
              dispatch(notifyMessage(translate('memberModifyChildHospitalAdmission.success.completionMessage')))
              history.push(memberUrl.url(), { tabValue: tabValue.child, childId })
            }
          }),
          isHiddenMain: false,
        })
      )
    },
    [addOperationLog]
  )

  const onFieldRemove = (i: number) => {
    formMethods.setValue(`${CATEGORY_KEY}.${i}.deleted`, true)
  }
  
  const onFieldAppend = () => {append({})}
  
  // 情報返却
  return {
    CATEGORY_KEY,
    SUB_CATEGORIES,
    formMethods,
    fields,
    hospitalAdmissionFlag,
    hospitalAdmissions,
    categoryMst,
    hasUsagePermission,
    onFieldAppend,
    onFieldRemove,
    onSubmit,
    childrenResultCode,
  }
}

/**
 * 入力フォームの初期値設定
 */
const getInitialInputs = (child: Child, childrenHospitalAdmission: GetChildrenHospitalAdmissionDto[]) => {
  const detailUpdateDatetime = child.detailUpdateDatetime
  const hospitalAdmissionFlag = child.hospitalAdmissionFlag
  const hospitalAdmission = childrenHospitalAdmission.map(({admissionAge, admissionMonth, ...others}) => ({
    admissionAge: admissionAge != null ? admissionAge.toString() : null,
      admissionMonth: admissionMonth != null ? admissionMonth.toString() : null,
      ...others
  }))

  return {
    detailUpdateDatetime,
    hospitalAdmissionFlag,
    hospitalAdmission
  }
}

/**
 * お子さま情報登録APIのINPUT用の入院情報を取得
 * @param data 入力値
 * @returns お子さま情報登録APIのINPUT用の入院情報
 */
export const getRegistHospitalAdmission = (data: Inputs) => {
  
  const base = {
    facilityId: DEFAULT_FACILITY_ID,
    hospitalAdmissionFlag: data.hospitalAdmissionFlag,
    detailUpdateDatetime: data.detailUpdateDatetime ?? "",
  }
  
  if (data.hospitalAdmissionFlag !== yesNo.yes || data.hospitalAdmission == null || data.hospitalAdmission.length === 0) {
    return {...base, hospitalAdmission: []}
  }
  
  const targetData = data.hospitalAdmission
    .map(({admissionAge, admissionMonth, ...others}) => ({
      admissionAge: admissionAge?.trim() ? Number(admissionAge.trim()) : null,
      admissionMonth: admissionMonth?.trim() ? Number(admissionMonth.trim()) : null,
      ...others
    }))
    .filter(e => !e.deleted && (e.admissionAge != null || e.admissionMonth != null || e.reason?.trim()))
                         
  // 年齢、月齢のいずれかのみnullなら0とする
  targetData.forEach(e => {
    if (e.admissionAge == null && e.admissionMonth != null) {
      e.admissionAge = 0
    } else if (e.admissionAge != null && e.admissionMonth == null) {
      e.admissionMonth = 0
    }
  })
  
  const comparer = (a: {admissionAge: number | null, admissionMonth: number | null} , b: {admissionAge: number | null, admissionMonth: number | null}) => {
    // 年齢、月齢のいずれかのみnullのデータはない
    // nullを含む比較
    if (a.admissionAge == null && b.admissionAge == null) {
      return 0
    } else if (a.admissionAge == null) {
      return 1
    } else if (b.admissionAge == null) {
      return -1
    }
    
    if (a.admissionAge !== b.admissionAge) {
      return a.admissionAge - b.admissionAge
    } else {
      return (a.admissionMonth ?? 0) - (b.admissionMonth ?? 0) // nullはないはずだけど型エラーになるのでnull対応
    }
  }
  
  targetData.sort(comparer)
  
  const hospitalAdmission = targetData.map((item, i) =>{
    return undefinedPropsToNull({
      facilityId: DEFAULT_FACILITY_ID,
      seq: i + 1,
      admissionAge: item.admissionAge,
      admissionMonth: item.admissionMonth,
      reason: item.reason
    })
  })
  
  return {...base, hospitalAdmission}
}